/**
 * How the start-workout flow holds the endpoint's answers, shared by the web
 * hook and the mobile one so both platforms degrade the same way.
 *
 * Two rules live here:
 *
 * - **Offline is the last answer, not an error.** The bolt is the one control
 *   that must always hand over a workout, so a failed request falls back to the
 *   last answer the app had, under a line saying how old it is. An error is
 *   reported only when nothing was ever remembered.
 * - **Late answers never re-sort.** A fresh answer that DIFFERS from the one on
 *   screen is held as `pending` and offered; the user chooses when to look. An
 *   identical one applies silently, and the first answer always applies.
 *
 * Kept pure so each platform's hook stays a lifecycle: it fetches, and folds
 * the result through these transitions.
 */

import { registerSessionScoped } from '../../../store/session-scope';

import type { StartWorkoutOptions } from '../../../types/domain/start-options.types';

/** An answer and when it arrived. */
export interface StartOptionsAnswer {
  options: StartWorkoutOptions;
  /** Epoch ms. */
  at: number;
}

/**
 * Everything the hook holds, in ONE object, so every transition is a single
 * pure updater. Splitting these would mean setting one piece of state from
 * inside another's updater, which React is free to run twice.
 */
export interface StartOptionsAnswerState {
  /** The answer on screen, fresh or remembered. */
  shown: StartOptionsAnswer | null;
  /** A newer, different answer, held rather than applied. */
  pending: StartOptionsAnswer | null;
  /** The last request failed and `shown` is the last answer the app had. */
  isStale: boolean;
  /** Set only when a request failed AND nothing was remembered. */
  error: string | null;
}

// =============================================================================
// THE REMEMBERED ANSWER
// =============================================================================

/**
 * The last answer the app had, kept for the length of the session.
 *
 * Module scope rather than component state on purpose: the flow is torn down
 * and rebuilt around every open, and the whole point of the offline frame is
 * that the bolt hands over a workout even when the network will not. One user
 * per session, so one slot is enough. A sign-out does NOT tear the module down
 * — nothing reloads the app — so the slot is emptied at the session boundary
 * below, or the next account would be offered this one's workout.
 */
let lastAnswer: StartOptionsAnswer | null = null;

/** Remember an answer that just arrived, and hand it back. */
export function rememberStartOptions(
  options: StartWorkoutOptions,
  at: number
): StartOptionsAnswer {
  lastAnswer = { options, at };
  return lastAnswer;
}

/** Drop the remembered answer. Exposed for tests, which share a module graph. */
export function clearStartWorkoutOptionsCache(): void {
  lastAnswer = null;
}

registerSessionScoped('startWorkoutOptions', clearStartWorkoutOptionsCache);

// =============================================================================
// TRANSITIONS
// =============================================================================

/** Where a hook starts: on the remembered answer, if there is one. */
export function initialAnswerState(): StartOptionsAnswerState {
  return { shown: lastAnswer, pending: null, isStale: false, error: null };
}

/** A request is going out: a previous error no longer stands. */
export function answerRequested(
  state: StartOptionsAnswerState
): StartOptionsAnswerState {
  return { ...state, error: null };
}

/**
 * Whether two answers would draw the same flow.
 *
 * A structural comparison rather than a field-by-field one: the payload carries
 * no timestamp of its own generation, so everything in it is meaningful and any
 * difference is a difference the flow would show.
 */
function same(a: StartWorkoutOptions, b: StartWorkoutOptions): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

/** An answer arrived. */
export function answerArrived(
  state: StartOptionsAnswerState,
  answer: StartOptionsAnswer
): StartOptionsAnswerState {
  // Nothing on screen yet, or the same answer again: apply it silently.
  if (state.shown === null || same(state.shown.options, answer.options)) {
    return { shown: answer, pending: null, isStale: false, error: null };
  }

  // `isStale` deliberately survives: the answer ON SCREEN is still the old one,
  // and clearing the line that says so while leaving the old facts up would
  // make the flow quietly dishonest. Accepting the pill clears both together.
  return { ...state, pending: answer, error: null };
}

/** The request failed. */
export function answerFailed(
  state: StartOptionsAnswerState,
  message: string
): StartOptionsAnswerState {
  // A remembered answer beats an error message: the user asked to start a
  // workout, and yesterday's list still starts one.
  const shown = state.shown ?? lastAnswer;
  if (shown) return { ...state, shown, isStale: true, error: null };

  return { ...state, error: message };
}

/** Swap the pending answer in. A no-op when nothing is waiting. */
export function pendingAnswerShown(
  state: StartOptionsAnswerState
): StartOptionsAnswerState {
  return state.pending
    ? { ...state, shown: state.pending, pending: null, isStale: false }
    : state;
}

/** The message for a failure, preferring the one the request carried. */
export function startOptionsFailureMessage(
  error: unknown,
  fallback: string
): string {
  return error instanceof Error ? error.message : fallback;
}
